import { motion } from "framer-motion";
import { useKey, QUALITY_LABEL } from "@/theory/key";

/**
 * The student-facing transposition control. Renders 12 chromatic tonic chips
 * in circle-of-fifths order, plus quality buttons (Major / Minor variants)
 * for whatever the lesson permits. Animates the active chip.
 */

export function KeyPicker({ compact = false }: { compact?: boolean }) {
  const k = useKey();
  if (!k) return null;
  const small = compact;
  return (
    <div className="my-3 rounded-2xl border border-cyan-400/30 bg-cyan-950/30 px-3 py-3">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
          ⤿ Transpose
        </div>
        <div className="font-mono text-[11px] text-cyan-300">
          Currently: <span className="text-cyan-100">{k.tonicName} {QUALITY_LABEL[k.quality]}</span>
        </div>
      </div>

      {/* Tonic names follow the current quality: A minor and C minor, not
          A♭ minor and D♭ minor. See MINOR_TONIC_TABLE in theory/key. */}
      <div className="flex flex-wrap gap-1.5">
        {k.tonicOptions.map((t) => {
          const active = t.pc === k.tonicPc;
          return (
            <motion.button
              key={t.name}
              layout
              onClick={() => k.setTonicPc(t.pc)}
              className={`relative rounded-md border px-2.5 py-1 font-mono text-[12px] font-bold tracking-wider transition-colors ${
                active
                  ? "border-amber-300 bg-amber-400 text-zinc-900"
                  : "border-cyan-400/30 bg-cyan-500/5 text-cyan-100/90 hover:bg-cyan-500/15"
              } ${small ? "h-7" : "h-8"}`}
              animate={{ scale: active ? 1.05 : 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 28 }}
            >
              {t.name}
              {active && (
                <motion.span
                  layoutId="key-pick-underline"
                  className="absolute inset-x-1 -bottom-0.5 h-0.5 rounded-full bg-amber-200"
                />
              )}
            </motion.button>
          );
        })}
      </div>

      {k.availableQualities.length > 1 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {k.availableQualities.map((q) => {
            const active = q === k.quality;
            return (
              <button
                key={q}
                onClick={() => k.setQuality(q)}
                className={`rounded-md border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                  active
                    ? "border-cyan-300 bg-cyan-400/30 text-cyan-50"
                    : "border-cyan-400/20 bg-cyan-500/5 text-cyan-200/80 hover:bg-cyan-500/15"
                }`}
              >
                {QUALITY_LABEL[q]}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
